/**
 * Nghỉ đột xuất + chuyển lịch (đợt 3 · 3.4, ADR-092, docs/plans/stt-3-4-tho-nghi-dot-xuat.md).
 *
 * MỘT tờ, ba bước, không đóng / mở lại giữa chừng:
 *   chon      — "Ai nghỉ hôm nay?" (chủ / quản lý): chạm tên;
 *   xac-nhan  — "Cho {tên} nghỉ hết hôm nay?" → "Cho nghỉ" (máy chủ tính "hôm nay", luôn lưu);
 *   ds        — "Còn {n} lịch cần xử lý": mỗi dòng MỘT chạm "Chuyển sang {tên}" (lễ tân cũng làm được).
 *
 * Chuyển từng lịch một qua `doi-gio` có sẵn (4 lớp chống trùng), không "Chuyển hết": máy không tự xếp thay chủ tiệm.
 * Mỗi dòng lỗi riêng — một dòng lỗi không làm hỏng dòng khác; ứng viên của dòng sau phụ thuộc kết quả dòng trước
 * nên chỉ chuyển MỘT dòng một lúc rồi tải lại danh sách.
 */
import { CalendarClock, CalendarOff, Phone, RefreshCw, UserRoundCheck, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type Api,
  ApiFailure,
  cauHinh,
  type DayBoard as DayBoardData,
  type DongLichVuong,
  gioPhut,
  hienSdt,
  ngayVn,
  tenDichVu,
  tenKhach,
} from './api.ts'
import { useChiNhanh } from './chi-nhanh-dang-dung.tsx'
import { type ThanDoiGio, thanDoiGio } from './doi-lich.ts'
import {
  buocMoDau,
  type DongHienThi,
  demChiNhanhKhac,
  ghepDong,
  huongCaTiem,
  khoaYDinh,
  laLoiCaTiem,
  laLoiCuoi,
  nguoiChoNghiDuoc,
  tieuDeDanhSach,
  tieuRongDanhSach,
  tongLichCanXuLy,
  tongLichNgoaiCa,
} from './nghi-dot-xuat.ts'
import { T } from './text/vi.ts'
import { Rong, ThongBao, To, Xuong } from './ui.tsx'

/** `ca-tiem` (v0.22.3 B1): "Cả tiệm nghỉ hôm nay?" — người này là người nhận khách cuối cùng, nên mời đóng cửa tiệm (K2). */
type Buoc = 'chon' | 'xac-nhan' | 'ds' | 'ca-tiem'

/**
 * Thân `doi-gio` của MỘT lần "Chuyển sang": cùng ngày + giờ, người mới, kèm ảnh chụp người bấm đã thấy (người, ngày,
 * giờ gốc). Thiếu ngày + giờ gốc thì lịch đã dời sang chỗ khác ở máy khác có thể bị kéo ngược (vòng sửa 2 · 3.4, A).
 */
export function thanChuyenLich(
  d: Pick<DongHienThi, 'dong' | 'lich'>,
  date: string,
  sangStaffId: string,
): ThanDoiGio {
  return thanDoiGio({ ...d.lich, staffId: d.dong.staffId, startMin: d.dong.startMin }, date, {
    date,
    staffId: sangStaffId,
    startMin: d.dong.startMin,
  })
}

const SO_GOI_DUOC = /^(\+84|0)\d{9}$/

interface Props {
  api: Api
  branchId: string
  /** Ngày đang xem (hôm nay khi bấm "Nghỉ đột xuất"; ngày bất kỳ khi mở từ dải đỏ). */
  date: string
  data: DayBoardData
  /** Bước mở đầu: `chon` từ nút "Nghỉ đột xuất", `ds` từ dải đỏ "Xử lý". */
  buocDau: 'chon' | 'ds'
  /** Vai `xep-ca` (chủ, quản lý): được cho nghỉ và huỷ nghỉ. Lễ tân chỉ chuyển lịch. */
  choNghi: boolean
  /** Vai `cau-hinh.tiem` (chủ, quản lý): đóng / mở lại cửa tiệm hôm nay (K2). Thiếu → chỉ hiện câu máy chủ. */
  choDongCua: boolean
  onDong: () => void
  /** Có thay đổi ở máy chủ — màn Lịch tải lại. */
  onDaDoi: () => void
  /** "Mở lịch" (chờ duyệt / nhiều dịch vụ): đóng tờ này, mở tờ chi tiết. */
  onMoLich: (a: DongHienThi['lich']) => void
  /** "Gọi khách đổi giờ": đóng tờ này, mở tờ Đổi giờ có sẵn. */
  onDoiGio: (a: DongHienThi['lich']) => void
}

export function ToNghiDotXuat(p: Props) {
  const { api, branchId, date, data } = p
  // Từ nút "Nghỉ đột xuất" mà chi nhánh chỉ còn MỘT người có ca chưa nghỉ: bỏ bước chọn tên, hỏi thẳng "Cả tiệm nghỉ hôm nay?".
  const [buoc, setBuoc] = useState<Buoc>(() => buocMoDau(p.buocDau, data.staff, data.nghi ?? []))
  const [chonId, setChonId] = useState<string | null>(null)
  const [dangLuu, setDangLuu] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  /** Lịch vướng ở chi nhánh khác (chỉ biết ngay sau khi cho nghỉ). */
  const [chiNhanhKhac, setChiNhanhKhac] = useState(0)
  /** Tên + câu do MÁY CHỦ báo khi người vừa chọn là người nhận khách cuối (chi nhánh có ≥ 2 người ở bảng nhưng chỉ một người nhận khách). */
  const [tuMayChu, setTuMayChu] = useState<{
    ten: string
    cau: string
    chiNhanhIds: string[]
  } | null>(null)
  const chiNhanhDs = useChiNhanh().ds
  /** G-N3: vừa bấm "Đóng cửa tiệm hôm nay" trong lần mở tờ này (hết khi bấm "Mở cửa lại hôm nay"). */
  const [vuaDongCua, setVuaDongCua] = useState(false)

  const nghi = data.nghi ?? []
  const ungChon = nguoiChoNghiDuoc(data.staff, nghi)
  const nguoi = ungChon.find((x) => x.id === chonId) ?? null
  const tenCaTiem =
    tuMayChu?.ten ?? (ungChon.length === 1 ? (ungChon[0]?.ten ?? '') : (nguoi?.ten ?? ''))
  // L-N1: chỉ đóng đúng chi nhánh máy chủ báo; báo chi nhánh khác chi nhánh đang xem → chỉ nói rõ, không có nút đóng.
  const caChiNhanhKhac =
    tuMayChu !== null && huongCaTiem(tuMayChu.chiNhanhIds, branchId) === 'chi-nhanh-khac'
  const tenChiNhanhBao =
    (tuMayChu?.chiNhanhIds ?? [])
      .map((id) => chiNhanhDs?.find((c) => c.id === id)?.name ?? T.offShopOtherBranchName)
      .join(', ') || T.offShopOtherBranchName
  const duocDongCua = p.choDongCua && !caChiNhanhKhac

  async function choNghiNguoiNay() {
    if (nguoi === null || dangLuu) return
    setDangLuu(true)
    setLoi(null)
    try {
      const r = await api.choNghiHomNay(nguoi.id)
      setChiNhanhKhac(demChiNhanhKhac(r.lichVuong, branchId))
      p.onDaDoi()
      setBuoc('ds')
    } catch (e) {
      if (laLoiCaTiem(e) && e instanceof ApiFailure) {
        // Người cuối cùng nhận khách: không cho nghỉ một mình — mời đóng cửa tiệm (K2).
        setTuMayChu({
          ten: e.tenNguoiCuoi ?? nguoi.ten,
          cau: e.messageVi,
          chiNhanhIds: e.chiNhanhNguoiCuoi,
        })
        setBuoc('ca-tiem')
      } else {
        setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
      }
    } finally {
      setDangLuu(false)
    }
  }

  /** "Đóng cửa tiệm hôm nay": đúng route K2 nên luật cọc / lịch vướng của ADR-111 tự áp; xong vào danh sách lịch cần gọi khách. */
  async function dongCuaTiem() {
    if (dangLuu) return
    setDangLuu(true)
    setLoi(null)
    try {
      await cauHinh.dongCua(branchId, { tuNgay: date, denNgay: date })
      setVuaDongCua(true)
      p.onDaDoi()
      setBuoc('ds')
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      setDangLuu(false)
    }
  }

  const tieuDe =
    buoc === 'ds'
      ? tieuDeDanhSach({
          daDongCua: vuaDongCua,
          tongLich: tongLichCanXuLy(nghi) + tongLichNgoaiCa(data.ngoaiCa ?? []),
        })
      : buoc === 'ca-tiem'
        ? T.offShopTitle
        : buoc === 'xac-nhan' && nguoi !== null
          ? T.offTodayConfirm(nguoi.ten)
          : T.offTodayTitle

  return (
    <To nhan={tieuDe} onDong={p.onDong}>
      <header className="to-dau">
        <h2>{tieuDe}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={p.onDong}
        >
          <X size={22} />
        </button>
      </header>

      {buoc === 'chon' && (
        <div className="to-than">
          <p className="chu-phu">{T.offTodayPickHint}</p>
          {ungChon.length === 0 ? (
            <Rong icon={<CalendarOff size={26} />} tieuDe={T.offTodayNobody} />
          ) : (
            <ul className="ds-chon-nghi">
              {ungChon.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className="nut nut-vien nut-lon nut-rong"
                    onClick={() => {
                      setChonId(n.id)
                      setLoi(null)
                      setBuoc('xac-nhan')
                    }}
                  >
                    {n.ten}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {buoc === 'xac-nhan' && nguoi !== null && (
        <>
          <div className="to-than">
            <p className="nghi-xac-nhan">{T.offTodayConfirmHint}</p>
            {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
          </div>
          <footer className="to-chan nghi-chan">
            <button
              type="button"
              className="nut nut-chinh nut-lon"
              disabled={dangLuu}
              onClick={() => void choNghiNguoiNay()}
            >
              {dangLuu ? T.offTodaySaving : T.offTodaySave}
            </button>
            <button
              type="button"
              className="nut nut-nhat nut-lon"
              disabled={dangLuu}
              onClick={() => {
                setLoi(null)
                setBuoc('chon')
              }}
            >
              {T.offTodayPickOther}
            </button>
          </footer>
        </>
      )}

      {buoc === 'ca-tiem' && (
        <>
          <div className="to-than">
            {/* Không có quyền đóng cửa (hiếm: chỉ chủ / quản lý có `xep-ca`): chỉ nói thật, không đưa nút làm không được. */}
            <p className="nghi-xac-nhan">
              {caChiNhanhKhac
                ? T.offShopOtherBranch(tenCaTiem, tenChiNhanhBao)
                : p.choDongCua
                  ? T.offShopHint(tenCaTiem)
                  : (tuMayChu?.cau ?? T.offShopNoRight)}
            </p>
            {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
          </div>
          <footer className="to-chan nghi-chan">
            {duocDongCua && (
              <button
                type="button"
                className="nut nut-chinh nut-lon"
                disabled={dangLuu}
                onClick={() => void dongCuaTiem()}
              >
                {dangLuu ? T.offShopSaving : T.offShopSave}
              </button>
            )}
            <button
              type="button"
              className={`nut nut-lon ${duocDongCua ? 'nut-nhat' : 'nut-chinh'}`}
              disabled={dangLuu}
              onClick={p.onDong}
            >
              {T.offShopKeep}
            </button>
          </footer>
        </>
      )}

      {buoc === 'ds' && (
        <DanhSachLichVuong
          api={api}
          branchId={branchId}
          date={date}
          data={data}
          choNghi={p.choNghi}
          choDongCua={p.choDongCua}
          chiNhanhKhac={chiNhanhKhac}
          daDongCua={vuaDongCua}
          onMoLai={() => setVuaDongCua(false)}
          onDong={p.onDong}
          onDaDoi={p.onDaDoi}
          onMoLich={p.onMoLich}
          onDoiGio={p.onDoiGio}
        />
      )}
    </To>
  )
}

function DanhSachLichVuong({
  api,
  branchId,
  date,
  data,
  choNghi,
  choDongCua,
  chiNhanhKhac,
  daDongCua,
  onMoLai,
  onDong,
  onDaDoi,
  onMoLich,
  onDoiGio,
}: {
  api: Api
  branchId: string
  date: string
  data: DayBoardData
  choNghi: boolean
  choDongCua: boolean
  chiNhanhKhac: number
  /** G-N3: vừa đóng cửa tiệm hôm nay — danh sách rỗng thì không nói "không có lịch nào cần chuyển". */
  daDongCua: boolean
  onMoLai: () => void
  onDong: () => void
  onDaDoi: () => void
  onMoLich: Props['onMoLich']
  onDoiGio: Props['onDoiGio']
}) {
  /** Chỉ người đổi được chi nhánh (> 1 chi nhánh chọn được) mới được dặn "đổi chi nhánh ở đầu màn" (ADR-101). */
  const chiNhanh = useChiNhanh()
  const [ds, setDs] = useState<DongLichVuong[] | null>(null)
  const [loiTai, setLoiTai] = useState(false)
  const [dangChuyen, setDangChuyen] = useState<string | null>(null)
  const [loiDong, setLoiDong] = useState<Record<string, string>>({})
  const [bao, setBao] = useState<string | null>(null)
  const [huyCho, setHuyCho] = useState<string | null>(null)
  const [dangHuy, setDangHuy] = useState(false)
  const [loiHuy, setLoiHuy] = useState<string | null>(null)
  /** Đã từng có dòng trong lần mở này: rỗng lúc sau = "đã xử lý hết", rỗng ngay từ đầu = "không có lịch nào". */
  const tungCoViec = useRef(false)
  const tenCuoi = useRef('')
  /** Khoá chống lặp THEO Ý ĐỊNH (lịch, người mới): bấm lại sau khi mạng chập chờn không thành hai lần chuyển. */
  const khoa = useRef(new Map<string, string>())
  const con = useRef(true)
  useEffect(() => {
    con.current = true
    return () => {
      con.current = false
    }
  }, [])

  const tai = useCallback(async () => {
    try {
      const r = await api.lichVuong(branchId, date)
      if (!con.current) return
      setDs(r.lich)
      setLoiTai(false)
    } catch {
      if (!con.current) return
      // Đã có danh sách thì giữ (số cũ vẫn hơn trắng); chưa có thì hiện "Tải lại".
      setLoiTai(true)
    }
  }, [api, branchId, date])
  useEffect(() => {
    void tai()
  }, [tai])

  const nghi = data.nghi ?? []
  const dong: DongHienThi[] =
    ds === null ? [] : ghepDong(ds, data.appointments, nghi, data.staff, data.ngoaiCa ?? [])
  if (dong.length > 0) {
    tungCoViec.current = true
    tenCuoi.current = dong[0]?.tenNguoiNghi ?? tenCuoi.current
  }

  async function chuyen(d: DongHienThi, sang: { staffId: string; ten: string }) {
    if (dangChuyen !== null) return
    const id = d.dong.appointmentId
    const k = khoaYDinh(id, sang.staffId)
    const kh = khoa.current.get(k) ?? crypto.randomUUID()
    khoa.current.set(k, kh)
    setDangChuyen(id)
    setLoiDong((c) => {
      const { [id]: _bo, ...con2 } = c
      return con2
    })
    setBao(null)
    try {
      await api.doiGio(id, thanChuyenLich(d, date, sang.staffId), kh)
      khoa.current.delete(k)
      if (con.current) setBao(T.offMoved(tenKhach(d.lich.customerName), sang.ten))
      onDaDoi()
    } catch (e) {
      const ma = e instanceof ApiFailure ? e.code : ''
      if (laLoiCuoi(ma)) khoa.current.delete(k)
      const chu = e instanceof ApiFailure ? e.messageVi : T.moveFailed
      if (con.current) setLoiDong((c) => ({ ...c, [id]: chu }))
    } finally {
      if (con.current) setDangChuyen(null)
      // Thành công hay lỗi: tải lại danh sách VÀ bảng ngày (tiêu đề "Còn n lịch" + dải đỏ đọc từ bảng ngày).
      onDaDoi()
      await tai()
    }
  }

  async function huyNghi(ngayNghiId: string) {
    setDangHuy(true)
    setLoiHuy(null)
    try {
      await cauHinh.huyNghi(ngayNghiId)
      setHuyCho(null)
      onDaDoi()
      await tai()
    } catch (e) {
      if (con.current) setLoiHuy(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      if (con.current) setDangHuy(false)
    }
  }

  const huyNguoi = nghi.find((n) => n.ngayNghiId === huyCho) ?? null
  const cauRong = tieuRongDanhSach({
    daDongCua,
    tungCoViec: tungCoViec.current,
    tenCuoi: tenCuoi.current,
  })

  /** "Mở cửa lại hôm nay": chỉ khi chi nhánh đang xem ĐÃ đóng cửa đúng hôm nay (vừa bấm "Đóng cửa tiệm hôm nay" ở bước trước). */
  const dongHomNay = choDongCua && data.dongCua != null && date === ngayVn()
  const [dangMoLai, setDangMoLai] = useState(false)
  async function moLaiHomNay() {
    if (dangMoLai) return
    setDangMoLai(true)
    setLoiHuy(null)
    try {
      await cauHinh.moLaiNgayDong(branchId, { tuNgay: date, denNgay: date })
      setBao(T.offShopReopenDone)
      onMoLai()
      onDaDoi()
      await tai()
    } catch (e) {
      if (con.current) setLoiHuy(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      if (con.current) setDangMoLai(false)
    }
  }

  return (
    <>
      <div className="to-than">
        {dongHomNay && data.dongCua != null && (
          <ThongBao
            loai="thong-tin"
            hanhDong={
              <button
                type="button"
                className="nut nut-vien nut-48"
                disabled={dangMoLai}
                onClick={() => void moLaiHomNay()}
              >
                {dangMoLai ? T.offTodaySaving : T.offShopReopen}
              </button>
            }
          >
            {T.closedBandReason(data.dongCua.lyDo)}
          </ThongBao>
        )}
        {choNghi && nghi.length > 0 && (
          <ul className="ds-dang-nghi">
            {nghi.map((n) => (
              <li key={n.ngayNghiId}>
                <CalendarOff size={18} />
                <span>{n.ten}</span>
                <button
                  type="button"
                  className="nut nut-vien nut-48"
                  disabled={dangHuy}
                  onClick={() => {
                    setHuyCho(n.ngayNghiId)
                    setLoiHuy(null)
                  }}
                >
                  {T.offUndo}
                </button>
              </li>
            ))}
          </ul>
        )}
        {huyNguoi !== null && (
          <ThongBao loai="canh-bao" lon>
            <b>{T.offUndoConfirm(huyNguoi.ten)}</b>
            <div className="nghi-hang-nut">
              <button
                type="button"
                className="nut nut-chinh nut-48"
                disabled={dangHuy}
                onClick={() => void huyNghi(huyNguoi.ngayNghiId)}
              >
                {dangHuy ? T.offTodaySaving : T.offUndoSave}
              </button>
              <button
                type="button"
                className="nut nut-nhat nut-48"
                disabled={dangHuy}
                onClick={() => setHuyCho(null)}
              >
                {T.offUndoKeep}
              </button>
            </div>
            {loiHuy !== null && <p className="nghi-loi-dong">{loiHuy}</p>}
          </ThongBao>
        )}

        {chiNhanhKhac > 0 && (
          <ThongBao loai="thong-tin">{T.offOtherBranch(chiNhanhKhac, chiNhanh.nhieu)}</ThongBao>
        )}
        {bao !== null && (
          <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
            {bao}
          </ThongBao>
        )}

        {/* Dòng lỗi mà dòng đó đã biến mất khỏi danh sách (người khác vừa xử lý xong): vẫn nói cho người bấm biết. */}
        {ds !== null &&
          Object.entries(loiDong)
            .filter(([id]) => !dong.some((d) => d.dong.appointmentId === id))
            .map(([id, chu]) => (
              <ThongBao
                key={id}
                loai="canh-bao"
                onDong={() =>
                  setLoiDong((c) => {
                    const { [id]: _bo, ...con2 } = c
                    return con2
                  })
                }
              >
                {chu}
              </ThongBao>
            ))}

        {ds === null && !loiTai && (
          <div style={{ display: 'grid', gap: 12 }}>
            {[0, 1, 2].map((i) => (
              <Xuong key={i} cao={96} />
            ))}
          </div>
        )}
        {loiTai && (
          <ThongBao
            loai="loi"
            hanhDong={
              <button type="button" className="nut nut-vien nut-48" onClick={() => void tai()}>
                <RefreshCw size={18} />
                {T.reload}
              </button>
            }
          >
            {T.offLoadFailed}
          </ThongBao>
        )}
        {ds !== null && dong.length === 0 && cauRong !== null && (
          <Rong icon={<UserRoundCheck size={26} />} tieuDe={cauRong} />
        )}

        {dong.length > 0 && (
          <ul className="ds-vuong-nghi">
            {dong.map((d) => (
              <DongVuong
                key={d.dong.appointmentId}
                d={d}
                dangChuyen={dangChuyen}
                loi={loiDong[d.dong.appointmentId] ?? null}
                onChuyen={(sang) => void chuyen(d, sang)}
                onMoLich={() => onMoLich(d.lich)}
                onDoiGio={() => onDoiGio(d.lich)}
              />
            ))}
          </ul>
        )}
      </div>
      <footer className="to-chan">
        <button type="button" className="nut nut-chinh nut-lon" onClick={onDong}>
          {T.offDone}
        </button>
      </footer>
    </>
  )
}

function DongVuong({
  d,
  dangChuyen,
  loi,
  onChuyen,
  onMoLich,
  onDoiGio,
}: {
  d: DongHienThi
  dangChuyen: string | null
  loi: string | null
  onChuyen: (sang: { staffId: string; ten: string }) => void
  onMoLich: () => void
  onDoiGio: () => void
}) {
  const { dong, lich } = d
  const dangDong = dangChuyen === dong.appointmentId
  const khoaHet = dangChuyen !== null
  const dau = dong.ungVien[0]
  const khac = dong.ungVien.slice(1)
  const goiDuoc = lich.customerPhone !== null && SO_GOI_DUOC.test(lich.customerPhone)
  return (
    <li className="dong-vuong">
      <div className="dong-vuong-dau">
        <b className="gio-vuong">{gioPhut(dong.startMin)}</b>
        <div className="thong-tin-vuong">
          <b>{tenKhach(lich.customerName)}</b>
          <span>{tenDichVu(lich.serviceName)}</span>
          <span className="chu-phu">{T.offRowOf(d.tenNguoiNghi)}</span>
          {/* BK-2: lý do vướng (người nghỉ thì như cũ, không nhãn). */}
          {dong.lyDo === 'ngoai-ca' && <span className="nhan-nho">{T.offTagOutside}</span>}
          {dong.lyDo === 'nghi-viec' && <span className="nhan-nho">{T.offTagStopped}</span>}
          {dong.lyDo === 'tiem-nghi' && <span className="nhan-nho">{T.offTagClosed}</span>}
          {/* B7: lịch chưa nhận vẫn chuyển người được — nói rõ nó đang chờ gì. */}
          {dong.trangThai === 'pending' && <span className="nhan-nho">{T.offTagPending}</span>}
          {/* "Chờ cọc" nổi bật hơn "Chờ nhận": khách có thể đã chuyển tiền thật, hạn giữ đang chạy. */}
          {dong.trangThai === 'awaiting_deposit' && (
            <span className="nhan-nho canh-bao">{T.offTagDeposit}</span>
          )}
        </div>
      </div>

      {dong.xuLy === 'cho-duyet' && (
        <>
          <p className="chu-phu">{T.offPending}</p>
          <button type="button" className="nut nut-vien nut-48 nut-rong" onClick={onMoLich}>
            {T.offOpen}
          </button>
        </>
      )}
      {dong.xuLy === 'nhieu-dich-vu' && (
        <>
          <p className="chu-phu">{T.offMulti}</p>
          <button type="button" className="nut nut-vien nut-48 nut-rong" onClick={onMoLich}>
            {T.offOpen}
          </button>
        </>
      )}
      {dong.xuLy === 'chuyen' && dau !== undefined && (
        <>
          <button
            type="button"
            className="nut nut-chinh nut-lon nut-rong"
            disabled={khoaHet}
            onClick={() => onChuyen(dau)}
          >
            {dangDong ? T.offMoving : T.offMoveTo(dau.ten)}
          </button>
          {khac.length > 0 && (
            <div className="nghi-khac">
              <span className="chu-phu">{T.offOthersFree}</span>
              <div className="hang-chip">
                {khac.map((u) => (
                  <button
                    key={u.staffId}
                    type="button"
                    className="nut nut-vien nut-48"
                    disabled={khoaHet}
                    onClick={() => onChuyen(u)}
                  >
                    {u.ten}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {dong.xuLy === 'chuyen' && dau === undefined && (
        <>
          {/* Ngày tiệm đóng cửa: không có chuyện "ai rảnh" — chỉ còn gọi khách đổi ngày. */}
          <p className="nghi-khong-ai">
            {dong.lyDo === 'tiem-nghi' ? T.offClosedCallDay : T.offNoOneFree}
          </p>
          <div className="nghi-hang-nut nghi-hang-goi">
            <button type="button" className="nut nut-vien nut-48" onClick={onDoiGio}>
              <CalendarClock size={18} />
              {T.offCallReschedule}
            </button>
            {goiDuoc && lich.customerPhone !== null && (
              <a className="nut nut-vien nut-48" href={`tel:${lich.customerPhone}`}>
                <Phone size={18} />
                {T.offCallPhone(hienSdt(lich.customerPhone))}
              </a>
            )}
          </div>
        </>
      )}
      {loi !== null && <p className="nghi-loi-dong">{loi}</p>}
    </li>
  )
}
